'use client';

import { CloseIcon } from '@insta-club/components';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

type ModalProps = {
    title: string;
    description?: string;
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    closeOnOverlayClick?: boolean;
};

export default function Modal({
    isOpen,
    onClose,
    children,
    title,
    description,
    closeOnOverlayClick = true
}: ModalProps) {
    useEffect(() => {
        if (!isOpen) return;

        const originalStyle = {
            overflow: document.body.style.overflow,
            paddingRight: document.body.style.paddingRight
        };

        const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${scrollBarWidth}px`;

        return () => {
            document.body.style.overflow = originalStyle.overflow;
            document.body.style.paddingRight = originalStyle.paddingRight;
        };
    }, [isOpen]);

    if (typeof window === 'undefined') return null;

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className='fixed inset-0 z-40 flex justify-center'
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <div
                        className='relative h-full w-full max-w-[var(--container-max)] bg-black/40 backdrop-blur-[2px]'
                        onClick={() => {
                            if (closeOnOverlayClick) onClose();
                        }}
                    >
                        <motion.div
                            className='absolute inset-0 z-50 flex items-center justify-center p-4'
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            transition={{ duration: 0.25, ease: 'easeOut' }}
                        >
                            <div
                                className='relative w-full rounded-2xl bg-white p-6 shadow-xl'
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className='mb-3 flex items-start justify-between gap-2'>
                                    <div className='space-y-2.5'>
                                        <div className='flex-1 truncate pl-1 text-base font-semibold text-gray-800'>
                                            {title}
                                        </div>
                                        {description && <p className='text-sm text-gray-600'>{description}</p>}
                                    </div>
                                    <div
                                        onClick={() => onClose()}
                                        className='cursor-pointer text-sm font-normal text-gray-700'
                                    >
                                        <CloseIcon />
                                    </div>
                                </div>
                                {children}
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
}

